import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Badge } from './ui/badge';
import { Clock, Crown, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FreelanceTrialBadge = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [freelanceStatus, setFreelanceStatus] = useState(null);

  useEffect(() => {
    if (user?.is_freelance || user?.role === 'freelance') {
      fetchFreelanceStatus();
    }
  }, [user]);

  const fetchFreelanceStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/freelance/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFreelanceStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch freelance status:', error);
    }
  };

  if (!freelanceStatus || !freelanceStatus.is_freelance) {
    return null;
  }

  // Independent Paid - No badge needed
  if (freelanceStatus.freelance_status === 'independent_paid') {
    return (
      <Badge 
        variant="outline" 
        className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-200"
      >
        <Crown className="w-3 h-3 mr-1" />
        Independent
      </Badge>
    );
  }

  // Represented Free - Show badge
  if (freelanceStatus.freelance_status === 'represented_free') {
    return (
      <Badge 
        variant="outline" 
        className="ml-2 bg-purple-50 text-purple-700 border-purple-200"
      >
        Represented
      </Badge>
    );
  }

  // Trial - Show days remaining
  if (freelanceStatus.is_trial) {
    const daysRemaining = freelanceStatus.days_remaining;
    
    // Trial ended - needs choice
    if (freelanceStatus.needs_choice) {
      return (
        <Badge 
          variant="destructive" 
          className="ml-2 cursor-pointer animate-pulse"
          onClick={() => navigate('/freelance/choose-model')}
        >
          <AlertTriangle className="w-3 h-3 mr-1" />
          Trial Ended - Choose Model
        </Badge>
      );
    }

    // Trial active - show days remaining
    const isUrgent = daysRemaining <= 3;
    return (
      <Badge 
        variant="outline" 
        className={`ml-2 cursor-pointer transition-all hover:scale-105 ${
          isUrgent 
            ? 'bg-amber-50 text-amber-700 border-amber-300' 
            : 'bg-blue-50 text-blue-700 border-blue-200'
        }`}
        onClick={() => navigate('/freelance/choose-model')}
        title="Click to choose your model early"
      >
        <Clock className="w-3 h-3 mr-1" />
        Independent Trial — {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
      </Badge>
    );
  }

  return null;
};

export default FreelanceTrialBadge;
